import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ScrollView,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
  Modal,
} from 'react-native';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { logout, incrementGuestMessageCount, incrementGuestPracticeCount } from '../store/authSlice';

interface ChatScreenProps {
  onNavigateToPractice: () => void;
  onNavigateToProfile: () => void;
  onMessagesChange?: (messages: Message[]) => void;
}
import AuthService from '../services/auth';
import LoginScreen from './LoginScreen';
import TeacherGuidance from './TeacherGuidance';
import SimpleTeacherTest from './SimpleTeacherTest';
import backendAI from '../services/ai';

type Message = {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
};

type ChatMode = 'daily' | 'practice';
type PracticePhase = 'overview' | 'round1' | 'round2' | 'round3' | 'completed';

type PracticeSession = {
  id: string;
  sourceContext: {
    category: string;
    situation: string;
    keyPhrases: string[];
    practiceValue: number;
    difficulty: string;
  };
  targetLanguage: string;
  exercises: Array<{
    type: string;
    nativePrompt: string;
    targetResponse: string;
    alternatives: string[];
    tips: string[];
    difficulty: string;
    points: number;
  }>;
  adaptiveLevel: number;
  estimatedDuration: number;
};

const ChatScreen: React.FC<ChatScreenProps> = ({ onNavigateToPractice, onNavigateToProfile, onMessagesChange }) => {
  const dispatch = useAppDispatch();
  const { user, isGuestMode, guestUsage } = useAppSelector((state) => state.auth);

  const [currentMode, setCurrentMode] = useState<ChatMode>('daily');
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputText, setInputText] = useState('');
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [score, setScore] = useState(0);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [readyForPractice, setReadyForPractice] = useState(false);
  const [lastUserMessage, setLastUserMessage] = useState<string>('');

  // 练习流程状态
  const [practicePhase, setPracticePhase] = useState<PracticePhase>('overview');
  const [currentPracticeSession, setCurrentPracticeSession] = useState<PracticeSession | null>(null);
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);

  const scrollViewRef = useRef<ScrollView>(null);

  useEffect(() => {
    // 初始化欢迎消息
    if (messages.length === 0) {
      addMessage('你好！我是你的语言学习伙伴。让我们聊聊你今天的事情吧！你今天过得怎么样？', false);
    }
  }, [messages.length, addMessage]);

  const addMessage = useCallback((text: string, isUser: boolean) => {
    const newMessage: Message = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      text,
      isUser,
      timestamp: new Date(),
    };

    setMessages(prevMessages => {
      const newMessages = [...prevMessages, newMessage];
      // 通知父组件消息变化
      onMessagesChange?.(newMessages);
      return newMessages;
    });

    // 添加消息到后端AI服务
    backendAI.addMessage({
      id: newMessage.id,
      text: newMessage.text,
      isUser: newMessage.isUser,
      timestamp: newMessage.timestamp,
    });

    // 滚动到底部
    setTimeout(() => {
      scrollViewRef.current?.scrollToEnd({ animated: true });
    }, 100);
  }, [onMessagesChange]);

  const handleSendMessage = () => {
    if (!inputText.trim()) {return;}

    // 检查游客模式限制
    if (isGuestMode) {
      if (currentMode === 'daily' && guestUsage.messageCount >= 5) {
        setShowLoginModal(true);
        return;
      }
      if (currentMode === 'practice' && guestUsage.practiceCount >= 5) {
        setShowLoginModal(true);
        return;
      }
    }

    // 记录用户最后一条消息
    setLastUserMessage(inputText);

    addMessage(inputText, true);

    // 增加使用计数
    if (isGuestMode) {
      if (currentMode === 'daily') {
        dispatch(incrementGuestMessageCount());
      } else {
        dispatch(incrementGuestPracticeCount());
      }
    }

    if (currentMode === 'daily') {
      handleDailyChat(inputText);
    } else {
      handlePracticeMode(inputText);
    }

    setInputText('');
  };

  const handleDailyChat = async (_userMessage: string) => {
    try {
      setIsAnalyzing(true);

      // 获取AI智能回复
      const reply = await backendAI.generateSmartReply();
      addMessage(reply, false);

      // 检查是否准备好练习
      if (messages.length > 4) {
        const ready = await backendAI.isReadyForPractice();
        setReadyForPractice(ready);

        if (ready && Math.random() > 0.6) {
          setTimeout(() => {
            addMessage('我觉得我们聊得差不多了，要不要试试用英语来表达一些内容？点击"F区 - 外语练习"开始！', false);
          }, 1500);
        }
      }
    } catch (error) {
      console.error('AI回复失败:', error);
      // 回退到原有的模拟回复
      const responses = [
        '听起来很有趣！能具体说说吗？',
        '那你当时是什么感受？',
        '然后呢？发生了什么？',
        '哇，听起来很棒！还有什么想分享的吗？',
      ];
      const randomResponse = responses[Math.floor(Math.random() * responses.length)];
      addMessage(randomResponse, false);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handlePracticeMode = async (userMessage: string) => {
    // 根据当前练习阶段处理用户回答
    if (practicePhase === 'overview') {
      // 概览阶段，检查是否是开始命令
      if (userMessage.includes('开始')) {
        startCurrentExercise();
      } else {
        addMessage('请输入"开始练习"来开始第一题！', false);
      }
      return;
    }

    // 评估当前练习的回答
    await evaluateCurrentExercise(userMessage);
  };

  const evaluateCurrentExercise = async (userAnswer: string) => {
    if (!currentPracticeSession || !currentPracticeSession.exercises) {return;}

    const currentExercise = currentPracticeSession.exercises[currentExerciseIndex];
    if (!currentExercise) {return;}

    const expectedAnswer = currentExercise.targetResponse;
    const points = currentExercise.points;

    // 简单评分逻辑（实际应该使用AI评估）
    const similarity = calculateSimilarity(userAnswer.toLowerCase(), expectedAnswer.toLowerCase());
    const isCorrect = similarity > 0.6;

    if (isCorrect) {
      const earnedPoints = Math.floor(points * Math.max(0.7, similarity));
      setScore(prev => prev + earnedPoints);

      let feedback = `很棒！+${earnedPoints}分 🎉\n\n✅ 标准答案：${expectedAnswer}`;

      // 显示备选答案
      if (currentExercise.alternatives && currentExercise.alternatives.length > 0) {
        feedback += `\n\n🔄 其他表达：${currentExercise.alternatives.slice(0, 2).join('、')}`;
      }

      addMessage(feedback, false);
    } else {
      let feedback = `不错的尝试！\n\n💡 建议表达：${expectedAnswer}`;

      // 显示提示和备选答案
      if (currentExercise.tips && currentExercise.tips.length > 1) {
        feedback += `\n\n📝 小提示：${currentExercise.tips[1] || currentExercise.tips[0]}`;
      }

      if (currentExercise.alternatives && currentExercise.alternatives.length > 0) {
        feedback += `\n\n🔄 其他表达：${currentExercise.alternatives.slice(0, 2).join('、')}`;
      }

      feedback += '\n\n你的回答也有merit，继续加油！';
      addMessage(feedback, false);
    }

    // 进入下一题
    setTimeout(() => {
      moveToNextExercise();
    }, 2000);
  };

  const calculateSimilarity = (str1: string, str2: string): number => {
    // 简单的相似度计算（实际应该使用更复杂的算法）
    const words1 = str1.split(' ');
    const words2 = str2.split(' ');
    const commonWords = words1.filter(word => words2.includes(word));
    return commonWords.length / Math.max(words1.length, words2.length);
  };

  const moveToNextExercise = () => {
    if (!currentPracticeSession || !currentPracticeSession.exercises) {return;}

    const totalExercises = currentPracticeSession.exercises.length;

    if (currentExerciseIndex + 1 < totalExercises) {
      // 继续下一题
      setCurrentExerciseIndex(prev => prev + 1);
      setTimeout(() => showCurrentExercise(), 500);
    } else {
      // 所有练习完成，显示最终总结
      setTimeout(() => showFinalSummary(), 2000);
    }
  };


  const showFinalSummary = () => {
    if (!currentPracticeSession) {return;}

    const totalExercises = currentPracticeSession.exercises.length;
    const totalPossiblePoints = currentPracticeSession.exercises.reduce((sum, ex) => sum + ex.points, 0);
    const percentage = totalPossiblePoints > 0 ? Math.round((score / totalPossiblePoints) * 100) : 0;

    let summaryText = '🎉 **练习完成！**\n\n📊 **成绩总结：**\n';
    summaryText += `完成练习：${totalExercises}道题目\n`;
    summaryText += `获得分数：${score}/${totalPossiblePoints}分\n`;
    summaryText += `正确率：${percentage}%\n\n`;
    summaryText += '🏆 **总评：';

    if (percentage >= 90) {
      summaryText += '优秀！你的英语表达能力很棒！🌟';
    } else if (percentage >= 70) {
      summaryText += '不错！继续练习会更好！💪';
    } else if (percentage >= 50) {
      summaryText += '还不错！多练习会有更大进步！📈';
    } else {
      summaryText += '加油！多练习会有进步的！📚';
    }

    summaryText += '**\n\n';

    // 显示学习建议
    if (currentPracticeSession.exercises.length > 0) {
      const exerciseTypes = [...new Set(currentPracticeSession.exercises.map(ex => ex.type))];
      summaryText += `📝 **本次练习内容：**${exerciseTypes.map(type => {
        const typeMap: { [key: string]: string } = {
          'translation': '翻译',
          'roleplay': '对话',
          'repetition': '跟读',
          'comprehension': '理解',
        };
        return typeMap[type] || type;
      }).join('、')}\n\n`;
    }

    summaryText += '点击"N区"继续聊天，或者"F区"开始新的练习！';

    addMessage(summaryText, false);
    setPracticePhase('completed');
  };

  const generateStructuredPracticeSession = async () => {
    try {
      setIsAnalyzing(true);

      // 提取用户的对话内容作为上下文
      const userMessages = messages.filter(msg => msg.isUser);
      const conversationSummary = userMessages.slice(-5).map(msg => msg.text).join(' ');

      const contextInfo = {
        conversation: conversationSummary,
        topics: userMessages.length > 0 ? ['daily_life', 'personal_experience'] : ['general'],
        message_count: userMessages.length,
      };

      // 获取结构化的三阶段练习
      const practiceSession = await backendAI.generatePracticeSession(contextInfo, 'en');

      if (practiceSession) {
        setCurrentPracticeSession(practiceSession);
        setPracticePhase('overview');

        // 显示练习概览
        showPracticeOverview(practiceSession);
      } else {
        // 降级到基础练习提示
        addMessage('根据你刚才的分享，我为你准备了一套个性化的英语练习。让我们开始吧！', false);
        setPracticePhase('round1');
        setCurrentExerciseIndex(0);
      }
    } catch (error) {
      console.error('生成结构化练习失败:', error);
      addMessage('让我们开始一些基础的英语练习吧！', false);
      setPracticePhase('round1');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const showPracticeOverview = (session: PracticeSession) => {
    let overviewText = '📚 **练习概览**\n\n';
    overviewText += `基于你的分享：${session.sourceContext.situation}\n\n`;

    overviewText += '📖 **今日练习内容：**\n';
    if (session.exercises && session.exercises.length > 0) {
      session.exercises.slice(0, 3).forEach((exercise, index) => {
        const exerciseTypeMap: { [key: string]: string } = {
          'translation': '翻译练习',
          'roleplay': '对话练习',
          'repetition': '跟读练习',
          'comprehension': '理解练习',
        };
        const typeName = exerciseTypeMap[exercise.type] || '练习';

        overviewText += `${index + 1}. ${typeName}\n`;
        overviewText += `   ${exercise.nativePrompt}\n`;

        // 显示学习提示
        if (exercise.tips && exercise.tips.length > 0) {
          overviewText += `   💡 提示：${exercise.tips[0]}\n`;
        }
        overviewText += '\n';
      });
    }

    overviewText += '🎯 **练习详情：**\n';
    overviewText += `📝 练习题目：${session.exercises?.length || 0}个\n`;
    overviewText += `🌟 难度级别：${session.sourceContext.difficulty}\n`;
    overviewText += `⏱️ 预计用时：${session.estimatedDuration || 10}-15分钟\n\n`;
    overviewText += '准备好了吗？输入"开始练习"开始第一题！';

    addMessage(overviewText, false);
  };

  const startCurrentExercise = () => {
    if (!currentPracticeSession || !currentPracticeSession.exercises || currentPracticeSession.exercises.length === 0) {
      addMessage('练习数据加载失败，请重试。', false);
      return;
    }

    setPracticePhase('round1');
    setCurrentExerciseIndex(0);

    // 显示第一个练习题目
    const exercise = currentPracticeSession.exercises[0];

    const exerciseTypeMap: { [key: string]: string } = {
      'translation': '翻译练习',
      'roleplay': '对话练习',
      'repetition': '跟读练习',
      'comprehension': '理解练习',
    };
    const typeName = exerciseTypeMap[exercise.type] || '练习';

    let exerciseText = '🎯 **第1题**\n\n';
    exerciseText += `**${typeName}**\n\n`;
    exerciseText += `${exercise.nativePrompt}\n\n`;

    if (exercise.tips && exercise.tips.length > 0) {
      exerciseText += `💡 **提示**: ${exercise.tips[0]}\n\n`;
    }

    exerciseText += `💯 **分值**: ${exercise.points}分\n\n`;
    exerciseText += '请输入你的答案：';

    addMessage(exerciseText, false);
  };


  const showCurrentExercise = () => {
    if (!currentPracticeSession || !currentPracticeSession.exercises) {return;}

    const exercise = currentPracticeSession.exercises[currentExerciseIndex];
    if (!exercise) {return;}

    const exerciseTypeMap: { [key: string]: string } = {
      'translation': '翻译练习',
      'roleplay': '对话练习',
      'repetition': '跟读练习',
      'comprehension': '理解练习',
    };
    const typeName = exerciseTypeMap[exercise.type] || '练习';

    let exerciseText = `🎯 **第${currentExerciseIndex + 1}题**\n\n`;
    exerciseText += `**${typeName}**\n\n`;
    exerciseText += `${exercise.nativePrompt}\n\n`;

    if (exercise.tips && exercise.tips.length > 0) {
      exerciseText += `💡 **提示**: ${exercise.tips[0]}\n\n`;
    }

    exerciseText += `💯 **分值**: ${exercise.points}分\n\n`;
    exerciseText += '请输入你的答案：';

    addMessage(exerciseText, false);
  };

  const switchMode = async (mode: ChatMode) => {
    setCurrentMode(mode);
    if (mode === 'practice') {
      // 重置练习状态
      setPracticePhase('overview');
      setCurrentExerciseIndex(0);
      setScore(0);

      addMessage('太好了！现在让我们开始个性化的英语练习。我会根据你刚才分享的内容为你定制学习计划：', false);
      setTimeout(() => {
        generateStructuredPracticeSession();
      }, 1000);
    } else {
      addMessage('让我们继续聊聊你今天的事情吧！', false);
      // 重置练习状态
      setPracticePhase('overview');
      setCurrentPracticeSession(null);
    }
  };

  const handleLogout = async () => {
    try {
      await AuthService.logout();
      dispatch(logout());
    } catch (error) {
      console.error('Logout error:', error);
      // 即使logout失败，也要清除本地状态
      dispatch(logout());
    }
  };

  const handleShowLogin = () => {
    setShowLoginModal(true);
  };

  const handleCloseLogin = () => {
    setShowLoginModal(false);
  };

  // 监听登录状态变化
  useEffect(() => {
    if (user && user.id !== 'anonymous' && showLoginModal) {
      setShowLoginModal(false);
    }
  }, [user, showLoginModal]);

  return (
    <View style={styles.container}>

      {/* 聊天区域 */}
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.chatContainer}
      >
        <ScrollView
          ref={scrollViewRef}
          style={styles.messagesContainer}
          contentContainerStyle={styles.messagesContent}
        >
          {messages.map((message) => (
            <View
              key={message.id}
              style={[
                styles.messageRow,
                message.isUser ? styles.userMessageRow : styles.aiMessageRow,
              ]}
            >
              <View
                style={[
                  styles.messageBubble,
                  message.isUser ? styles.userBubble : styles.aiBubble,
                ]}
              >
                <Text
                  style={[
                    styles.messageText,
                    message.isUser ? styles.userText : styles.aiText,
                  ]}
                >
                  {message.text}
                </Text>
              </View>
            </View>
          ))}

          {/* AI思考中状态 */}
          {isAnalyzing && (
            <View style={[styles.messageRow, styles.aiMessageRow]}>
              <View style={[styles.messageBubble, styles.aiBubble, styles.thinkingBubble]}>
                <Text style={[styles.messageText, styles.aiText, styles.thinkingText]}>
                  AI正在思考... 💭
                </Text>
              </View>
            </View>
          )}
        </ScrollView>

        {/* 输入区域 */}
        <View style={styles.inputContainer}>
          <TextInput
            style={styles.textInput}
            value={inputText}
            onChangeText={setInputText}
            placeholder={currentMode === 'daily' ? '说说你的想法...' : '输入英语翻译...'}
            multiline
            maxLength={500}
            onSubmitEditing={handleSendMessage}
            blurOnSubmit={false}
            returnKeyType="send"
            autoCorrect={false}
            autoCapitalize="none"
            keyboardType="default"
            textContentType="none"
            importantForAutofill="no"
            showSoftInputOnFocus={true}
            focusable={true}
            editable={true}
          />
          <TouchableOpacity
            style={[styles.sendButton, !inputText.trim() && styles.sendButtonDisabled]}
            onPress={handleSendMessage}
            disabled={!inputText.trim()}
          >
            <Text style={styles.sendButtonText}>发送</Text>
          </TouchableOpacity>
        </View>
      </KeyboardAvoidingView>

      {/* 登录模态框 */}
      <Modal
        visible={showLoginModal}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={handleCloseLogin}
      >
        <View style={styles.modalContainer}>
          <View style={styles.modalHeader}>
            <TouchableOpacity onPress={handleCloseLogin}>
              <Text style={styles.modalCloseButton}>关闭</Text>
            </TouchableOpacity>
          </View>
          {isGuestMode && (guestUsage.messageCount >= 5 || guestUsage.practiceCount >= 5) && (
            <View style={styles.guestLimitMessage}>
              <Text style={styles.guestLimitTitle}>🎉 体验完毕</Text>
              <Text style={styles.guestLimitText}>
                您已使用完免费体验额度
              </Text>
              <Text style={styles.guestLimitText}>
                登录后可以无限制使用所有功能！
              </Text>
            </View>
          )}
          <LoginScreen />
        </View>
      </Modal>

      {/* 底部导航栏 */}
      <View style={styles.bottomNavigation}>
        <TouchableOpacity
          style={styles.navButton}
          onPress={onNavigateToPractice}
          activeOpacity={0.7}
        >
          <Text style={styles.navButtonIcon}>🚀</Text>
          <Text style={styles.navButtonText}>练习</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={styles.navButton}
          onPress={onNavigateToProfile}
          activeOpacity={0.7}
        >
          <Text style={styles.navButtonIcon}>👤</Text>
          <Text style={styles.navButtonText}>个人</Text>
        </TouchableOpacity>
      </View>

      {/* 浮动翻译练习按钮 */}
      {readyForPractice && (
        <TouchableOpacity
          style={styles.floatingButton}
          onPress={onNavigateToPractice}
          activeOpacity={0.8}
        >
          <Text style={styles.floatingButtonText}>🚀</Text>
          <Text style={styles.floatingButtonLabel}>翻译练习</Text>
        </TouchableOpacity>
      )}

      {/* 简单教师测试 */}
      <SimpleTeacherTest />

      {/* 教师指导系统（暂时禁用）
      <TeacherGuidance
        messageCount={messages.length}
        practiceReady={readyForPractice}
        currentScreen="chat"
        lastUserMessage={lastUserMessage}
        onNavigateToPractice={onNavigateToPractice}
      />
      */}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f8f9fa',
  },
  chatContainer: {
    flex: 1,
  },
  messagesContainer: {
    flex: 1,
    paddingHorizontal: 20,
  },
  messagesContent: {
    paddingTop: 20,
    paddingBottom: 10,
  },
  messageRow: {
    marginBottom: 15,
  },
  userMessageRow: {
    alignItems: 'flex-end',
  },
  aiMessageRow: {
    alignItems: 'flex-start',
  },
  messageBubble: {
    maxWidth: '80%',
    paddingHorizontal: 15,
    paddingVertical: 10,
    borderRadius: 20,
  },
  userBubble: {
    backgroundColor: '#3498db',
  },
  aiBubble: {
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#e9ecef',
  },
  thinkingBubble: {
    backgroundColor: '#f0f0f0',
    borderColor: '#d0d0d0',
  },
  messageText: {
    fontSize: 16,
    lineHeight: 22,
  },
  userText: {
    color: '#fff',
  },
  aiText: {
    color: '#2c3e50',
  },
  thinkingText: {
    fontStyle: 'italic',
    color: '#666',
  },
  inputContainer: {
    flexDirection: 'row',
    paddingHorizontal: 20,
    paddingVertical: 15,
    backgroundColor: '#fff',
    borderTopWidth: 1,
    borderTopColor: '#e9ecef',
    alignItems: 'flex-end',
  },
  textInput: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#e9ecef',
    borderRadius: 20,
    paddingHorizontal: 15,
    paddingVertical: 10,
    marginRight: 10,
    maxHeight: 100,
    fontSize: 16,
    backgroundColor: '#f8f9fa',
    color: '#2c3e50',
  },
  sendButton: {
    backgroundColor: '#3498db',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 20,
  },
  sendButtonDisabled: {
    backgroundColor: '#bdc3c7',
  },
  sendButtonText: {
    color: '#fff',
    fontWeight: '600',
    fontSize: 16,
  },
  modalContainer: {
    flex: 1,
    backgroundColor: '#f0f0f0',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    padding: 20,
    paddingTop: 50,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  modalCloseButton: {
    fontSize: 16,
    color: '#007AFF',
    fontWeight: '600',
  },
  guestLimitMessage: {
    backgroundColor: '#e8f5e8',
    margin: 20,
    padding: 20,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: '#4CAF50',
    alignItems: 'center',
  },
  guestLimitTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#2E7D32',
    marginBottom: 10,
  },
  guestLimitText: {
    fontSize: 14,
    color: '#388E3C',
    textAlign: 'center',
    lineHeight: 20,
    marginBottom: 5,
  },
  floatingButton: {
    position: 'absolute',
    bottom: 90,
    right: 20,
    backgroundColor: '#27ae60',
    borderRadius: 30,
    paddingHorizontal: 16,
    paddingVertical: 12,
    alignItems: 'center',
    justifyContent: 'center',
    elevation: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    flexDirection: 'row',
  },
  floatingButtonText: {
    fontSize: 18,
    marginRight: 6,
  },
  floatingButtonLabel: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
  },
  bottomNavigation: {
    flexDirection: 'row',
    backgroundColor: '#ffffff',
    borderTopWidth: 1,
    borderTopColor: '#e9ecef',
    paddingVertical: 8,
    paddingHorizontal: 16,
    paddingBottom: 20, // 为底部安全区域留出空间
    shadowColor: '#000',
    shadowOffset: { width: 0, height: -2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 8,
  },
  navButton: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: 8,
    borderRadius: 8,
  },
  navButtonIcon: {
    fontSize: 20,
    marginBottom: 4,
  },
  navButtonText: {
    fontSize: 12,
    color: '#666',
    fontWeight: '500',
  },
});

export default ChatScreen;
